import { createAsyncThunk } from "@reduxjs/toolkit";
import apiConfig from "constants/apiConfig";
import { bookmarkApplciation as bookmarkApplciationAPI, fetchApplication as fetchApplicationAPI, fetchApplicationMedia, fetchCampaignApplications as fetchCampaignApplicationsAPI } from "services/api/campaign/application";
import { AuthState } from "slices/auth/reducer";
import { ERROR } from "types";
import { ApplicationPosts, CampaignApplication, CampaignApplications, CampaignApplicationSlice } from "types/campaign/applications";
import { generateUniqueKey, getErrorMessage, isFresh } from "utils/helpers";

export const fetchCampaignApplications = createAsyncThunk<{ campaignApplications: CampaignApplications, filters: { page: number, [key: string]: any }, filterId: string, campaignId: string }, { campaignId: string, force?: boolean, filters: { page: number, [key: string]: any } }, { rejectValue: string }>(
    'campaign/applications',
    async ({ campaignId, force, filters }, { getState, rejectWithValue }) => {
        try {
            const STALE_TIME = apiConfig.FETCH_CAMPAIGN_APPLICATIONS.staleTime;
            const filterId = generateUniqueKey(filters, ['page']);
            const state = getState() as { auth: AuthState, campaignApplications: CampaignApplicationSlice };
            if (filters.page === 1 && !force && isFresh(state.campaignApplications.campaigns[campaignId]?.filtered_lists?.[filterId]?.last_fetched_at ?? null, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }
            const agencyId = state.auth.selectedAgency?.uuid ?? '';
            const campaignApplications = await fetchCampaignApplicationsAPI(agencyId, campaignId, filters);
            return { campaignApplications, filters, filterId, campaignId };
        } catch (error) {
            return rejectWithValue('Failed to fetch campaign applications');
        }
    }
)

export const fetchApplication = createAsyncThunk<{ application: CampaignApplication, influencerId: string, campaignId: string }, { campaignId: string, influencerId: string, force?: boolean }, { rejectValue: string }>(
    'campaign/application',
    async ({ campaignId, influencerId, force }, { getState, rejectWithValue }) => {
        try {
            const STALE_TIME = apiConfig.FETCH_APPLICATION.staleTime;
            const state = getState() as { auth: AuthState, campaignApplications: CampaignApplicationSlice };

            if (!force && isFresh(state.campaignApplications.campaigns[campaignId]?.applications?.[influencerId]?.last_fetched_at ?? null, STALE_TIME)) {
                rejectWithValue(ERROR.CACHED);
            }
            const agencyId = state.auth.selectedAgency?.uuid ?? '';
            const application = await fetchApplicationAPI(agencyId, campaignId, influencerId);
            return { application, campaignId, influencerId };

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch application'));
        }
    }
)

export const bookmarkApplciation = createAsyncThunk<{ campaignId: string, influencerId: string, bookmarked: 'y' | 'n' }, { campaignId: string, influencerId: string, bookmark: 'y' | 'n' }, { rejectValue: string }>(
    'campaign/bookmark',
    async ({ campaignId, influencerId, bookmark }, { getState, rejectWithValue }) => {
        try {
            const state = getState() as { auth: AuthState }

            const agencyId = state.auth.selectedAgency?.uuid;
            if (!agencyId) throw new Error("No agency selected!")
            
            const status = await bookmarkApplciationAPI(agencyId, campaignId, influencerId, bookmark);

            if(!status) throw new Error("Failed to bookmark application");

            return {campaignId, influencerId, bookmarked: bookmark};
                
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to bookmark application'));
        }
    }
)

export const fetchApplicationPosts = createAsyncThunk<{ posts: ApplicationPosts, influencerId: string, campaignId: string }, { campaignId: string, influencerId: string, force?: boolean }, { rejectValue: string }>(
    'campaign/application/post',
    async ({ campaignId, influencerId, force }, { getState, rejectWithValue }) => {
        try {
            const STALE_TIME = apiConfig.FETCH_APPLICATION_POSTS.staleTime;
            const state = getState() as { auth: AuthState, campaignApplications: CampaignApplicationSlice };

            if (!force && isFresh(state.campaignApplications.posts[campaignId]?.[influencerId]?.last_fetched_at ?? null, STALE_TIME)) {
                rejectWithValue(ERROR.CACHED);
            }
            const agencyId = state.auth.selectedAgency?.uuid ?? '';
            const posts = await fetchApplicationMedia(agencyId, campaignId, influencerId);
            return { posts, campaignId, influencerId };

        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch application posts'));
        }
    }
)